import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateFaqMutation, useUpdateFaqMutation } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableFaqRow = {
  id?: number | string;
  key?: number | string;
  question_fr?: string;
  question_en?: string;
  answer_fr?: string;
  answer_en?: string;
};

type FaqPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  mode?: "create" | "edit";
  row?: EditableFaqRow | null;
  onSaved?: () => Promise<void> | void;
};

const FaqPopup: React.FC<FaqPopupProps> = ({
  isOpen,
  onClose,
  request,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createFaq] = useCreateFaqMutation();
  const [updateFaq] = useUpdateFaqMutation();
  const isEditMode = mode === "edit";

  const { control, reset, getValues, watch } = useCustomForm<{
    question_fr: string;
    question_en: string;
    answer_fr: string;
    answer_en: string;
  }>({
    defaultValues: {
      question_fr: "",
      question_en: "",
      answer_fr: "",
      answer_en: "",
    },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      question_fr: isOpen ? String(row?.question_fr ?? "") : "",
      question_en: isOpen ? String(row?.question_en ?? "") : "",
      answer_fr: isOpen ? String(row?.answer_fr ?? "") : "",
      answer_en: isOpen ? String(row?.answer_en ?? "") : "",
    });
  }, [isOpen, reset, row?.question_fr, row?.question_en, row?.answer_fr, row?.answer_en]);

  const questionFr = watch("question_fr");
  const answerFr = watch("answer_fr");
  const canSave = String(questionFr ?? "").trim().length > 0 && String(answerFr ?? "").trim().length > 0;

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? row?.key;
    if (isEditMode && rowId == null) return;

    const { question_fr, question_en, answer_fr, answer_en } = getValues();
    const baseUrl = request.url.replace(/\/+$/, "").replace(/\/[^/]+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    const payload: Record<string, unknown> = {
      question_fr: String(question_fr ?? ""),
      question_en: String(question_en ?? ""),
      answer_fr: String(answer_fr ?? ""),
      answer_en: String(answer_en ?? ""),
      ...(isEditMode ? { id: rowId } : {}),
    };

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateFaq({ url, data: payload }).unwrap();
      } else {
        await createFaq({ url, data: payload }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier FAQ" : "Nouvelle FAQ"}
      onClose={onClose}
      portal
      fixSize="medium"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving || !canSave}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <Input dataLength="autofit" label={t("question_fr")} name="question_fr" control={control} type="text" size="large" />
            <Input dataLength="autofit" label={t("question_en")} name="question_en" control={control} type="text" size="large" />
            <Input dataLength="autofit" label={t("answer_fr")} name="answer_fr" control={control} type="textarea" size="large" />
            <Input dataLength="autofit" label={t("answer_en")} name="answer_en" control={control} type="textarea" size="large" />
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default FaqPopup;
